import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { Heading } from "components/designV2/heading/heading";
import { PageHeaderRow } from "components/designV2/layout/pageLayout";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import { SegmentController } from "components/designV2/segmentcontroller/segmentcontroller";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ControlsGrid,
  DropdownsCell,
  OptionsCell,
  ToggleCell,
  Toolbar,
} from "./scheduleToolbar.styled";
import {
  UnitTeamDropdown,
  type UnitTeamOption,
} from "web-apps/company/components/common/filters/unitTeamDropdown.component";

import {
  GroupByOption,
  IntervalOption,
  SizeOption,
} from "../hooks/useScheduleToolbarFilters";

interface Props {
  groupByOption: GroupByOption;
  intervalOption: IntervalOption;
  sizeOption: SizeOption;
  unitTeamOptions: UnitTeamOption[];
  selectedWorkGroupIds: string[];
  onSelectedWorkGroupIdsChange: (values: string[]) => void;
  personOptions: Array<{ value: string; label: string }>;
  unitTeamEmptyText: string;
  personEmptyText: string;
  selectedPersonIds: string[];
  onSelectedPersonIdsChange: (values: string[]) => void;
  onScheduleClick: () => void;
  onGroupByChange: (value: GroupByOption) => void;
  onIntervalChange: (value: IntervalOption) => void;
  onSizeChange: (value: SizeOption) => void;
  onOptionsClick: () => void;
}

const INTERVAL_OPTIONS: Array<{ value: IntervalOption; labelKey: string }> = [
  { value: "weekly", labelKey: "Schedule.Weekly" },
  { value: "monthly", labelKey: "Schedule.Monthly" },
];

const buildSegmentOptions = <T extends string>(
  options: Array<{ value: T; labelKey: string }>,
  translate: (key: string) => string,
): Array<{ value: T; label: string }> =>
  options.map((option) => ({
    value: option.value,
    label: translate(option.labelKey),
  }));

export const ScheduleToolbar: React.FC<Props> = (props) => {
  const { t } = useTranslation();

  const intervalOptions = useMemo(
    () => buildSegmentOptions(INTERVAL_OPTIONS, t),
    [t],
  );

  const handleIntervalChange = (value: string): void => {
    if (value === "weekly" || value === "monthly") {
      props.onIntervalChange(value);
    }
  };

  return (
    <Toolbar>
      <PageHeaderRow>
        <Heading level="h2">{t("Schedule.Scheduling")}</Heading>
        <PrimaryButton
          label={t("Schedule.Schedule")}
          iconName="v2/plus"
          onClick={props.onScheduleClick}
        />
      </PageHeaderRow>
      <ControlsGrid>
        <ToggleCell>
          <SegmentController
            options={intervalOptions}
            value={props.intervalOption}
            onChange={handleIntervalChange}
          />
        </ToggleCell>
        <DropdownsCell>
          <UnitTeamDropdown
            label={t("Schedule.UnitTeam")}
            placeholder={t("Schedule.AllUnitsTeamsVisible")}
            emptyText={props.unitTeamEmptyText}
            options={props.unitTeamOptions}
            selectedTeamIds={props.selectedWorkGroupIds}
            onSelectedTeamIdsChange={props.onSelectedWorkGroupIdsChange}
          />
          <SearchableDropdown
            label={t("Schedule.Person")}
            placeholder={t("Schedule.SearchOrBrowse")}
            emptyText={props.personEmptyText}
            options={props.personOptions}
            leadingIconName="v2/search"
            multiSelect
            selectedValues={props.selectedPersonIds}
            onSelectedValuesChange={props.onSelectedPersonIdsChange}
            onClear={() => props.onSelectedPersonIdsChange([])}
          />
        </DropdownsCell>
        <OptionsCell>
          <SecondaryButton
            label={t("Schedule.Options")}
            size="small"
            iconName="v2/settings"
            onClick={props.onOptionsClick}
          />
        </OptionsCell>
      </ControlsGrid>
    </Toolbar>
  );
};
